import type { MutableRefObject, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import type { FlyToOptions } from '@elfsight/widget-map';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import type { StoreLocatorLocation } from '../types';
import { FLY_TO_OPTIONS_WITH_CARD_OFFSET } from './constants';
import { useMapAPI } from './map-api-context';
import { useUserLocation } from './user-location-context';

export type IActiveLocationContext = {
  activeLocation?: StoreLocatorLocation;
  prevActiveLocationRef?: MutableRefObject<StoreLocatorLocation | undefined>;
  setActiveLocation: (id?: string) => void;
};

const ActiveLocationContext = createContext<IActiveLocationContext>({
  activeLocation: undefined,
  prevActiveLocationRef: undefined,
  setActiveLocation: noop
});

export function ActiveLocationProvider({ children }: PropsWithChildren) {
  const [activeLocation, setActiveLocation] = useState<
    StoreLocatorLocation | undefined
  >(undefined);
  const [activeLocationId, setActiveLocationId] = useState<
    string | undefined
  >();
  const prevActiveLocationRef = useRef(activeLocation);
  const { editingItems, editMode, detailCardEnabled } = useSettings();
  const { mapAPIRef, points } = useMapAPI();
  const { locations } = useUserLocation();

  useEffect(() => {
    const foundLocation = locations.find(
      (location) => location.id === activeLocationId
    );

    setActiveLocation((prevLocation) => {
      prevActiveLocationRef.current = prevLocation;
      return foundLocation;
    });

    if (!foundLocation) {
      handleSetActiveLocation(undefined);
    }
  }, [locations, activeLocationId]);

  const handleSetActiveLocation = useCallback(
    (id?: string) => {
      if (!mapAPIRef?.current) {
        return;
      }

      if (id === undefined) {
        setActiveLocationId(undefined);
        mapAPIRef.current.setActiveMarker(undefined);
        return;
      }

      const isMarker = id !== 'user_location';
      const nextPoint = points.find((point) => point.id === id);

      if (!nextPoint?.coordinates) {
        return;
      }

      const willBeOpen = isMarker && detailCardEnabled;
      const flyToOptions: FlyToOptions = willBeOpen
        ? FLY_TO_OPTIONS_WITH_CARD_OFFSET
        : {};

      if (isMarker) {
        setActiveLocationId(id);
        mapAPIRef.current.setActiveMarker(nextPoint.id);
      }

      setTimeout(() => {
        mapAPIRef?.current?.flyTo(
          {
            center: nextPoint.coordinates
          },
          flyToOptions
        );
      }, 0);
    },
    [points, mapAPIRef, detailCardEnabled]
  );

  const editingLocationId = editingItems && editingItems['location'];

  useEffect(() => {
    if (
      editMode &&
      editingLocationId &&
      typeof editingLocationId === 'string'
    ) {
      handleSetActiveLocation(editingLocationId);
    }
  }, [editMode, editingLocationId, handleSetActiveLocation]);

  return (
    <ActiveLocationContext.Provider
      value={{
        setActiveLocation: handleSetActiveLocation,
        activeLocation,
        prevActiveLocationRef
      }}
    >
      {children}
    </ActiveLocationContext.Provider>
  );
}

export const useActiveLocation = () => {
  return useContext(ActiveLocationContext);
};
